import { useState, useRef, useEffect } from 'react'
import { Plus, Trash2, ClipboardList } from 'lucide-react'
import type { ATALineItem, Surcharge, APris } from '../lib/api/types'
import { nettoBelop, bruttoBelop, formatSEK } from '../utils/calculations'

const UNITS = ['st', 'tim', 'm', 'm²', 'm³', 'kg', 'ton', 'ls']

interface LineItemsGridProps {
  lineItems: ATALineItem[]
  surcharges: Surcharge[]
  apriser?: APris[]
  onChange: (items: ATALineItem[]) => void
}

export default function LineItemsGrid({ lineItems, surcharges, apriser = [], onChange }: LineItemsGridProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const addRow = () => {
    const nextOrder = lineItems.length > 0
      ? Math.max(...lineItems.map(i => i.sortOrder)) + 1
      : 1
    onChange([...lineItems, {
      sortOrder: nextOrder,
      specifikation: '',
      datum: '',
      mangd: null,
      enhet: '',
      prisPerEnhet: null,
      paslagKod: null,
    }])
  }

  const addFromAPris = (item: APris) => {
    const nextOrder = lineItems.length > 0
      ? Math.max(...lineItems.map(i => i.sortOrder)) + 1
      : 1
    onChange([...lineItems, {
      sortOrder: nextOrder,
      specifikation: item.benamning || '',
      datum: '',
      mangd: null,
      enhet: item.enhet || '',
      prisPerEnhet: item.pris ?? null,
      paslagKod: null,
    }])
    setPickerOpen(false)
  }

  const removeRow = (sortOrder: number) => {
    onChange(lineItems.filter(i => i.sortOrder !== sortOrder))
  }

  const updateRow = (sortOrder: number, field: keyof ATALineItem, value: string) => {
    onChange(lineItems.map(item => {
      if (item.sortOrder !== sortOrder) return item
      if (field === 'mangd' || field === 'prisPerEnhet') {
        return { ...item, [field]: value === '' ? null : parseFloat(value) }
      }
      if (field === 'paslagKod') {
        return { ...item, [field]: value === '' ? null : parseInt(value, 10) }
      }
      return { ...item, [field]: value }
    }))
  }

  const activeSurcharges = surcharges.filter(s => s.typ && s.typ.trim())
  const activeApriser = apriser.filter(a => a.benamning && a.benamning.trim())

  return (
    <div>
      <div className="table-container line-items-table" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              <th>Specifikation</th>
              <th style={{ width: 110 }}>Datum</th>
              <th style={{ width: 80 }} className="num">Mängd</th>
              <th style={{ width: 90 }}>Enh.</th>
              <th style={{ width: 110 }} className="num">Pris/enh</th>
              <th style={{ width: 120 }} className="num">Nettobelopp</th>
              <th style={{ width: 100 }}>Påslag</th>
              <th style={{ width: 120 }} className="num">Bruttobelopp</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                  Inga rader — klicka "+ Lägg till rad"
                </td>
              </tr>
            )}
            {lineItems.map((item, idx) => {
              const net   = nettoBelop(item)
              const gross = bruttoBelop(item, surcharges)
              return (
                <tr key={item.sortOrder}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    <input
                      value={item.specifikation}
                      onChange={e => updateRow(item.sortOrder, 'specifikation', e.target.value)}
                      placeholder="Beskriv arbete / material"
                      style={{ minWidth: 160 }}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={item.datum ?? ''}
                      onChange={e => updateRow(item.sortOrder, 'datum', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.mangd ?? ''}
                      onChange={e => updateRow(item.sortOrder, 'mangd', e.target.value)}
                      placeholder="0"
                      style={{ textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    <input
                      list="line-item-units"
                      value={item.enhet ?? ''}
                      onChange={e => updateRow(item.sortOrder, 'enhet', e.target.value)}
                      placeholder="enh"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.prisPerEnhet ?? ''}
                      onChange={e => updateRow(item.sortOrder, 'prisPerEnhet', e.target.value)}
                      placeholder="0"
                      style={{ textAlign: 'right' }}
                    />
                  </td>
                  <td className="computed">{net !== 0 ? formatSEK(net) : '–'}</td>
                  <td>
                    <select
                      value={item.paslagKod ?? ''}
                      onChange={e => updateRow(item.sortOrder, 'paslagKod', e.target.value)}
                    >
                      <option value="">Inget</option>
                      {activeSurcharges.map(s => (
                        <option key={s.sortOrder} value={s.sortOrder}>
                          {s.sortOrder}: {s.typ.length > 20 ? s.typ.slice(0, 20) + '…' : s.typ}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="computed" style={{ fontWeight: gross !== net ? 600 : 400 }}>
                    {gross !== 0 ? formatSEK(gross) : '–'}
                  </td>
                  <td>
                    <button
                      className="icon-button danger"
                      onClick={() => removeRow(item.sortOrder)}
                      title="Ta bort rad"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <datalist id="line-item-units">
        {UNITS.map(u => <option key={u} value={u} />)}
      </datalist>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
        <button className="btn-secondary" onClick={addRow}>
          <Plus size={15} />Lägg till rad
        </button>

        <div ref={pickerRef} style={{ position: 'relative' }}>
          <button className="btn-secondary" onClick={() => setPickerOpen(o => !o)}>
            <ClipboardList size={15} />Från à-prislista
          </button>
          {pickerOpen && (
            <div className="apris-picker">
              {activeApriser.length === 0 ? (
                <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--ink-faint)' }}>
                  Inga poster i à-prislistan.<br />
                  <a href="/prislista" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                    Öppna à-prislistan →
                  </a>
                </div>
              ) : (
                activeApriser.map(item => (
                  <button
                    key={item.sortOrder}
                    className="apris-picker-item"
                    onClick={() => addFromAPris(item)}
                  >
                    <span>{item.benamning}</span>
                    <span className="apris-picker-meta">
                      {item.pris != null ? item.pris.toLocaleString('sv-SE') + ' kr' : '–'} / {item.enhet || '–'}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
